import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge, RiskPill, fmtDate, fmtCurrency } from "../lib/status";
import { CalendarDays, Search } from "lucide-react";
import AccountDangerZone from "../components/AccountDangerZone";

const UPCOMING_STATUSES = ["awaiting_staff_approval", "awaiting_customer_confirmation", "confirmed"];

const FILTERS = [
  { value: "all", label: "All" },
  { value: "upcoming", label: "Upcoming" },
  { value: "awaiting_customer_confirmation", label: "Awaiting your confirmation" },
  { value: "confirmed", label: "Confirmed" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No show" },
  { value: "change_requested", label: "Change requested" },
];

export default function CustomerBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/customer/bookings").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (filter === "change_requested") out = out.filter((b) => b.change_requested);
    else if (filter === "upcoming") out = out.filter((b) => UPCOMING_STATUSES.includes(b.status));
    else if (filter !== "all") out = out.filter((b) => b.status === filter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((b) =>
        b.booking_reference?.toLowerCase().includes(s)
        || b.company?.name?.toLowerCase().includes(s)
        || b.vehicle?.name?.toLowerCase().includes(s));
    }
    return out;
  }, [items, filter, q]);

  return (
    <div className="space-y-6" data-testid="portal-bookings-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">My Bookings</h1>
        <p className="text-slate-600 mt-1">All bookings linked to your email address, across every business that uses ConfirmPH.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="portal-bookings-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search reference, business, vehicle…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm w-72 max-w-full"
          />
        </div>
        <div className="flex flex-wrap gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={`portal-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === f.value ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500" data-testid="portal-bookings-empty">
            <CalendarDays className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-2">No bookings here yet.</p>
            <p className="text-xs mt-1">Bookings appear automatically when a business creates one with your email.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {filtered.map((b) => (
            <li key={b.id} className="p-4 hover:bg-slate-50" data-testid={`portal-booking-row-${b.id}`}>
              <Link to={`/portal/bookings/${b.id}`} className="block">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{b.company?.name || "Unknown business"}</span>
                      <span className="text-xs text-slate-500 font-mono">{b.booking_reference}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      {b.vehicle?.name || "-"} · {fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.risk_status && b.risk_status !== "ok" && <RiskPill risk={b.risk_status} />}
                    <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                                 cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                                 reconfirmationRequired={b.reconfirmation_required} />
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-500">{fmtCurrency(b.price)}</div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <AccountDangerZone />
    </div>
  );
}
